import { useMemo, useRef } from "react";
import { useDataChannel } from "@livekit/components-react";
import { toast } from "sonner";
import type { TFunction } from "i18next";
import { twinService } from "../api/twin.api";
import {
  extractTwinRequest,
  parseTwinDataMessage,
  shouldShowTwinPrompt,
} from "../lib/twinPrompt";

/**
 * Listens on the LiveKit data-channel topic "twin" for a `twin.request`
 * event — published by the backend when Mochi (the assistant's meeting
 * twin) is dispatched into a RESTRICTED room and needs an in-room approval
 * before it can join. Shows a sonner toast with Approve/Deny actions to
 * every eligible participant (anyone who may admit — the same
 * `may_admit_guests` signal the waiting-room gate already uses).
 *
 * The decode + eligibility decision is kept in the pure
 * `shouldShowTwinPrompt`/`extractTwinRequest` helpers (`../lib/twinPrompt`)
 * so it's unit-testable without a live LiveKit room; this hook is just the
 * live wiring (data channel in, toast + approve/deny mutation out).
 *
 * Must be called inside a <LiveKitRoom> provider.
 */
export function useTwinApprovalPrompt(canAdmit: boolean, t: TFunction): void {
  const decoder = useMemo(() => new TextDecoder(), []);
  // Latest `canAdmit` without re-subscribing the data channel on every render.
  const canAdmitRef = useRef(canAdmit);
  canAdmitRef.current = canAdmit;

  useDataChannel("twin", (msg) => {
    const message = parseTwinDataMessage(decoder, msg.payload);
    if (!shouldShowTwinPrompt(message, canAdmitRef.current)) return;
    const request = message && extractTwinRequest(message);
    if (!request) return;
    const { dispatchId, twinName } = request;

    toast(t("call.twin.requestPrompt", { name: twinName }), {
      action: {
        label: t("call.twin.approve"),
        onClick: () => {
          void twinService.approve(dispatchId);
        },
      },
      cancel: {
        label: t("call.twin.deny"),
        onClick: () => {
          void twinService.deny(dispatchId);
        },
      },
    });
  });
}
